import { FetchHelper } from "shared-ts-utils-fetch";
import { AccessToken } from "shared-ts-utils-authentication";
import { CustomerEnvironmentName } from "firefly-shared-ts-customer-environment";
import { anonymizeStreetAddress, SensorId } from "firefly-shared-ts-domain";
import { ChimneySensorStatusQuery } from "./ChimneySensorStatusQuery";
import { ChimneySensorStatusDto } from "./ChimneySensorStatus";
import {
  IllegalStateError,
  NotFoundError,
  NullError,
} from "shared-ts-utils-core";
import { QueryDto, QueryResult } from "shared-ts-utils-query";

const anonymize = localStorage.getItem("anonymize") === "true";

export class ChimneySensorStatusDao {
  private readonly fetchHelper: FetchHelper;

  constructor(args: {
    accessToken: AccessToken;
    customerEnvironment: CustomerEnvironmentName;
  }) {
    this.fetchHelper = new FetchHelper({
      basePath: "/api/chimney-sensor-status/",
    })
      .authorizationBearer(args.accessToken)
      .addHeader("Firefly-Environment", args.customerEnvironment);
  }

  getById(id: SensorId): Promise<ChimneySensorStatusDto> {
    const query = new ChimneySensorStatusQuery({
      id: id,
    });
    return this.querySingle(query).then((result) => {
      if (result === undefined) {
        throw new NotFoundError("Did not find ChimneySensor with id: " + id);
      } else {
        return result;
      }
    });
  }

  updateSensorStatus(): Promise<void> {
    return this.fetchHelper.post("update-sensor-status").fetch();
  }

  query(query: QueryDto): Promise<QueryResult<ChimneySensorStatusDto>> {
    return this.fetchHelper
      .post("query", query)
      .fetchAsJson<QueryResult<ChimneySensorStatusDto>>()
      .then((result) => {
        if (anonymize) {
          return {
            ...result,
            result: result.result.map((it) => {
              return {
                ...it,
                streetAddress: anonymizeStreetAddress(
                  it.streetAddress,
                  it.flueId
                ),
              };
            }),
          };
        }
        return result;
      });
  }

  querySingle(
    query: ChimneySensorStatusQuery
  ): Promise<ChimneySensorStatusDto | undefined> {
    return this.fetchHelper
      .post("query", query.toDto())
      .fetchAsJson<QueryResult<ChimneySensorStatusDto>>()
      .then((response) => {
        if (response.result == null) {
          throw new NullError(
            "QueryResult contains not data: \n" + JSON.stringify(response)
          );
        }
        if (response.result.length === 0) {
          return undefined;
        }
        if (response.result.length > 1) {
          throw new IllegalStateError(
            `Query single ChimneySensor ${JSON.stringify(
              query
            )}, unexpectedly resulted in more than one: ` +
              response.result.length
          );
        }

        return response.result[0];
      });
  }
}
